﻿import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, KeyRound, User, UserCircle2 } from 'lucide-react';
import {
  updateStaffSchema,
  type ApiError,
  type UpdateStaffInput,
} from '@tracker/shared';
import { usersService } from '@/services/users.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

function SectionHeader({
  icon,
  title,
  optional,
}: {
  icon: React.ReactNode;
  title: string;
  optional?: boolean;
}) {
  return (
    <CardHeader className="border-b border-border/40 px-4 py-2.5">
      <CardTitle className="flex items-center gap-2 text-sm font-semibold text-primary">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
          {icon}
        </div>
        {title}
        {optional && (
          <span className="ml-auto text-xs font-normal text-muted-foreground">Optional</span>
        )}
      </CardTitle>
    </CardHeader>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive">{message}</p>;
}

export function StaffEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['staff', id],
    queryFn: () => usersService.get(id),
    enabled: !!id,
  });

  // â”€â”€ Basic info form (users table) â”€â”€
  const { register, trigger, getValues, reset, formState, control } = useForm<UpdateStaffInput>({
    resolver: zodResolver(updateStaffSchema),
  });

  useEffect(() => {
    if (data) {
      reset({
        email: data.user.email,
        employee_code: data.user.employee_code ?? '',
        first_name: data.user.first_name ?? '',
        last_name: data.user.last_name ?? '',
        phone: data.user.phone,
        is_active: data.user.is_active,
      });
    }
  }, [data, reset]);

  const update = useMutation({
    mutationFn: (input: UpdateStaffInput) => usersService.update(id, input),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const isSaving = update.isPending;

  // â”€â”€ Set password (independent action) â”€â”€
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  const setPwd = useMutation({
    mutationFn: () => usersService.setPassword(id, newPwd),
    onSuccess: () => {
      toast.success('Password updated');
      setNewPwd('');
      setConfirmPwd('');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pwdTooShort = newPwd.length > 0 && newPwd.length < 8;
  const pwdMismatch = confirmPwd.length > 0 && newPwd !== confirmPwd;
  const pwdValid = newPwd.length >= 8 && newPwd === confirmPwd;

  async function handleSaveAll() {
    if (!(await trigger())) {
      toast.error('Please fix the highlighted fields before saving.');
      return;
    }
    await update.mutateAsync(getValues());
    toast.success('Staff updated');
    void queryClient.invalidateQueries({ queryKey: ['staff'] });
    navigate(`/hrms/staff/${id}`);
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <div className="h-14 rounded-xl bg-muted animate-pulse" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg border border-border/60 bg-card p-4 space-y-3">
            <div className="h-4 w-32 rounded bg-muted animate-pulse" />
            <div className="grid gap-3 md:grid-cols-2">
              <div className="h-9 rounded-md bg-muted animate-pulse" />
              <div className="h-9 rounded-md bg-muted animate-pulse" />
              <div className="h-9 rounded-md bg-muted animate-pulse" />
              <div className="h-9 rounded-md bg-muted animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const { errors } = formState;
  const avatarUrl = data.new_photos_url ?? data.profile?.avatar_url;

  return (
    <div className="space-y-4">

      {/* â”€â”€ Page header â”€â”€ */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 px-2"
            onClick={() => navigate(`/hrms/staff/${id}`)}
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-primary/20 bg-primary/5">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <UserCircle2 className="h-6 w-6 text-primary/40" />
            )}
          </div>
          <div>
            <h1 className="text-lg font-bold leading-tight">
              {data.user.first_name} {data.user.last_name}
            </h1>
            <p className="text-xs text-muted-foreground">
              #{data.user.employee_code ?? '—'} · Edit Profile
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/hrms/staff/${id}`)}>
            Cancel
          </Button>
          <Button onClick={() => void handleSaveAll()} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* â”€â”€ Basic Info â”€â”€ */}
      <Card>
        <SectionHeader icon={<User className="h-3.5 w-3.5" />} title="Basic Info" />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="first_name">First Name</Label>
              <Input id="first_name" placeholder="John" {...register('first_name')} />
              <FieldError message={errors.first_name?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Last Name</Label>
              <Input id="last_name" placeholder="Doe" {...register('last_name')} />
              <FieldError message={errors.last_name?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="john@example.com" {...register('email')} />
              <FieldError message={errors.email?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="employee_code">Employee Code</Label>
              <Input id="employee_code" placeholder="EMP001" {...register('employee_code')} />
              <FieldError message={errors.employee_code?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" placeholder="9876543210" {...register('phone')} />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ? 'true' : 'false'}
                    onValueChange={(v) => field.onChange(v === 'true')}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* â”€â”€ Security / Set Password â”€â”€ */}
      <Card>
        <SectionHeader icon={<KeyRound className="h-3.5 w-3.5" />} title="Set Password" optional />
        <CardContent className="p-4 space-y-4">
          <p className="text-xs text-muted-foreground">
            Set a new login password for this employee. This signs them out of all devices.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
              />
              {pwdTooShort && <FieldError message="Password must be at least 8 characters" />}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <Input
                id="confirm_password"
                type="password"
                autoComplete="new-password"
                placeholder="Re-enter password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
              />
              {pwdMismatch && <FieldError message="Passwords do not match" />}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={!pwdValid || setPwd.isPending}
            onClick={() => setPwd.mutate()}
          >
            <KeyRound className="h-4 w-4" />
            {setPwd.isPending ? 'Updating...' : 'Set Password'}
          </Button>
        </CardContent>
      </Card>

    </div>
  );
}
